import type { RealProfile } from '../types/profiles';

export interface RealGeneralAnalytics {
  totalParticipants: number;
  totalRecords: number;
  featureUsage: { name: string; value: number; pct: number }[];
  topFeature: { name: string; pct: number } | null;
}

export function computeRealGeneralAnalytics(profiles: RealProfile[]): RealGeneralAnalytics {
  let communication = 0;
  let routines = 0;
  let games = 0;
  let drawings = 0;
  let music = 0;

  for (const profile of profiles) {
    communication += profile.subcategoryClicks.reduce((s, c) => s + c.clickCount, 0);
    routines += profile.subRoutines.length;
    games += profile.gameMetrics.reduce((s, g) => s + g.attempts, 0);
    drawings += profile.progressStats?.drawingsRecognizedTotal ?? 0;
    music += profile.musicPlays.reduce((s, m) => s + m.playCount, 0);
  }

  const raw = [
    { name: 'Comunicação alternativa', value: communication },
    { name: 'Rotinas', value: routines },
    { name: 'Jogos', value: games },
    { name: 'Desenho com IA', value: drawings },
    { name: 'Música', value: music },
  ];
  const total = raw.reduce((s, f) => s + f.value, 0);
  const featureUsage = raw
    .map((f) => ({ ...f, pct: total > 0 ? Math.round((f.value / total) * 100) : 0 }))
    .sort((a, b) => b.value - a.value);

  const totalRecords = communication + routines + games + drawings + music;

  return {
    totalParticipants: profiles.length,
    totalRecords,
    featureUsage,
    topFeature: featureUsage[0] ? { name: featureUsage[0].name, pct: featureUsage[0].pct } : null,
  };
}

/** Qual funcionalidade um único participante mais usou — mesmo cálculo de
 *  `computeRealGeneralAnalytics`, mas por perfil em vez de somado. `null`
 *  quando o perfil não tem nenhum registro de atividade (schema novo,
 *  professor/pai individual — ver `RealProfile.source`). */
export function topFeatureForProfile(profile: RealProfile): { name: string; value: number } | null {
  const communication = profile.subcategoryClicks.reduce((s, c) => s + c.clickCount, 0);
  const routines = profile.subRoutines.length;
  const games = profile.gameMetrics.reduce((s, g) => s + g.attempts, 0);
  const drawings = profile.progressStats?.drawingsRecognizedTotal ?? 0;
  const music = profile.musicPlays.reduce((s, m) => s + m.playCount, 0);

  const candidates = [
    { name: 'Comunicação alternativa', value: communication },
    { name: 'Rotinas', value: routines },
    { name: 'Jogos', value: games },
    { name: 'Desenho com IA', value: drawings },
    { name: 'Música', value: music },
  ];
  const top = candidates.reduce((best, c) => (c.value > best.value ? c : best), candidates[0]);
  return top.value > 0 ? top : null;
}
